import Fuse from 'fuse.js';
import get from 'lodash/get';
import filter from 'lodash/filter';
import find from 'lodash/find';
import flatten from 'lodash/flatten';
import uniqBy from 'lodash/uniqBy';
import { slugify } from '../../utils/common';

/* eslint-disable import/prefer-default-export */
export class Api {
  data;

  index;

  constructor(data) {
    if (!window.dataLayer) window.dataLayer = [];
    this.data = data;
    this.decorateData();
    this.index = new Fuse(this.data.listings, {
      shouldSort: true,
      isCaseSensitive: false,
      includeScore: true,
      useExtendedSearch: true,
      keys: [
        {
          name: 't',
          weight: 2,
        },
        {
          name: 'department',
          weight: 1.5,
        },
        'geo.region',
        'geo.site', // country
        'geo.state',
        'geo.city',
        'geo.location',
      ],
    });
  }

  decorateData = () => {
    const reduceGeo = (geo, item, level, result) =>
      geo.reduce((acc, data) => {
        let children = [];
        let label = null;
        let nextLevel = null;

        if (data.sites) {
          children = data.sites;
          nextLevel = 'site'; // country
        } else if (data.states) {
          children = data.states;
          nextLevel = 'state';
        } else if (data.cities) {
          children = Object.entries(data.cities).map(([city, locations]) => ({
            id: city,
            locations: Array.isArray(locations) ? locations : [locations],
          }));
          nextLevel = 'city';
        } else if (data.locations) {
          children = data.locations;
          nextLevel = 'location';
        }

        if (children.length > 0) {
          switch (level) {
            case 'region':
              label = this.lookupRegion(data.id);
              break;

            case 'site': // country
              label = this.lookupCountry(data.id);
              break;

            case 'state':
              label = data.name;
              break;

            default:
              label = data.id;
              break;
          }
          return reduceGeo(children, { ...item, [level]: label }, nextLevel, acc);
        }

        acc[data] = {
          ...item,
          [level]: this.lookupLocation(data),
        };

        return acc;
      }, result);

    const reduceDepartmentSlugs = (departments) => {
      return Object.keys(departments).reduce((departmentSlugs, departmentId) => {
        const departmentName = departments[departmentId];
        const departmentSlug = slugify(departmentName);
        return [
          ...departmentSlugs,
          {
            id: departmentId,
            name: departmentName,
            slug: departmentSlug,
          },
        ];
      }, []);
    };

    this.data.lookup.geo = reduceGeo(this.data.geo, {}, 'region', {});
    this.data.lookup.departmentSlugs = reduceDepartmentSlugs(
      get(this.data, 'lookup.departments', [])
    );

    this.data.listings = this.data.listings.map((listing) => ({
      ...listing,
      department: this.lookupDepartment(listing.dp),
      geo: this.lookupGeo(listing.l),
    }));
    // Remove any duplicates from the list.
    this.data.listings = uniqBy(this.data.listings, 'id');
  };

  lookupGeo = (id) => get(this.data, `lookup.geo[${id}]`, {});

  lookupRegion = (id) => get(this.data, `lookup.regions[${id}]`, '');

  lookupDepartment = (id) => get(this.data, `lookup.departments[${id}]`, '');

  lookupDepartmentSlugById = (id) =>
    find(get(this.data, 'lookup.departmentSlugs', []), { id })?.slug;

  lookupDepartmentIdBySlug = (slug) =>
    find(get(this.data, 'lookup.departmentSlugs', []), { slug })?.id;

  lookupCountry = (id) => get(this.data, `lookup.sites[${id}]`, '');

  lookupLocation = (id) => get(this.data, `lookup.locations[${id}]`, '');

  lookupType = (id) => get(this.data, `lookup.types[${id}]`, '');

  getRegions = () => get(this.data, `lookup.regions`, {});

  getCountries = () => get(this.data, `lookup.sites`, {});

  getGeographical = () => get(this.data, `geo`, []);

  getDepartments = () => get(this.data, `departments`, {});

  getTypes = () => get(this.data, `lookup.types`, {});

  getLocations = () => get(this.data, `lookup.locations`, {});

  findCountry = (countryCode) => {
    // look in geo for country data.
    const geo = get(this.data, `geo`, []);

    // eslint-disable-next-line no-restricted-syntax
    for (const region of geo) {
      const data = find(region.sites, { id: countryCode });
      if (data) return data;
    }

    return false;
  };

  /**
   * Determine if a given countryCode is part of a certain region (by id).
   * @param countryCode
   * @param regionId
   * @returns {boolean}
   */
  isCountryInRegion = (countryCode, regionId) => {
    const geo = get(this.data, `geo`, []);

    // eslint-disable-next-line no-restricted-syntax
    for (const region of geo) {
      if (region.id === regionId) {
        return !!find(region.sites, { id: countryCode });
      }
    }

    return false;
  };

  countryHasStates = (countryCode) => {
    const countryData = this.findCountry(countryCode);

    return countryData.states && countryData.states.length;
  };

  getStatesInCountry = (country) => {
    let countryData = country;
    if (typeof countryData !== 'object') {
      countryData = this.findCountry(country);
    }

    return countryData.states || null;
  };

  getLocationIDsInCountry = (country, location = '', state = '') => {
    let countryData = country;
    if (typeof countryData !== 'object') {
      countryData = this.findCountry(country);
    }

    if (countryData && countryData.cities) {
      if (location === '') {
        // Find all city IDs in the country
        return flatten(Object.values(countryData.cities));
      }

      // Find IDs for specific location in specific country
      return countryData.cities[location] || [];
    }
    if (countryData && countryData.states) {
      if (state === '') {
        // Find all city IDs in all states
        return countryData.states.reduce(
          (allCities, { cities = [] }) => [...allCities, ...flatten(Object.values(cities))],
          []
        );
      }

      const { cities: stateCities = {} } = find(countryData.states, { id: state });
      if (location === '') {
        // Find city IDs for specific state
        return flatten(Object.values(stateCities));
      }

      // Find IDs for specific location in specific state in specific country
      return stateCities[location] || [];
    }

    return [];
  };

  getCityNamesInCountry = (country, state = '') => {
    let countryData = country;
    if (typeof countryData !== 'object') {
      countryData = this.findCountry(country);
    }

    if (countryData && countryData.cities) {
      return Object.keys(countryData.cities);
    }
    if (countryData && countryData.states) {
      if (state === '') {
        return countryData.states.reduce(
          (allCities, { cities = [] }) => [...allCities, ...Object.keys(cities)],
          []
        );
      }

      const { cities: stateCities = {} } = find(countryData.states, { id: state });

      return Object.keys(stateCities);
    }

    return [];
  };

  getJobs = (selectedFilters) => {
    const all = get(this.data, `listings`, []);
    const geo = get(this.data, `geo`, []);
    const {
      location = '',
      department = '',
      query = '',
      site: country = '',
      region = '',
      state = '',
      type = '',
    } = selectedFilters;
    let locationFilter = null;
    let departmentFilter = null;
    const queryFilter = query !== '' ? query : null;
    const typeFilter = type !== '' ? type : null;

    if (country !== '') {
      locationFilter = this.getLocationIDsInCountry(country, location, state);
    } else if (region !== '') {
      const regionData = find(geo, ({ id }) => id === region);

      if (regionData) {
        // Find all locations within region
        locationFilter = regionData.sites.reduce(
          (allLocations, regionCountry) => [
            ...allLocations,
            ...this.getLocationIDsInCountry(regionCountry),
          ],
          []
        );
      } else {
        return [];
      }
    }

    if (department !== '') {
      departmentFilter = department;
    }

    if (queryFilter && queryFilter.match(/^[0-9]+$/)) {
      const jobById = find(all, { id: queryFilter });

      if (jobById) {
        return [jobById];
      }
    }

    const filteredData = filter(all, (job) => {
      if (departmentFilter && job.dp !== departmentFilter) return false;
      if (typeFilter && job.y !== parseInt(typeFilter, 10)) return false;
      if (locationFilter) {
        if (typeof locationFilter === 'string' && job.l !== locationFilter) return false;
        if (Array.isArray(locationFilter) && locationFilter.indexOf(job.l) < 0) return false;
      }

      return true;
    });

    if (queryFilter && queryFilter.length > 0) {
      this.index.setCollection(filteredData);
      return this.index
        .search({
          $and: queryFilter
            .split(' ')
            .filter((w) => w)
            .map((word) => {
              const or = [];
              const containsWord = `'${word}`;
              or.push({ t: containsWord });
              or.push({ department: containsWord });
              or.push({ 'geo.region': containsWord });
              or.push({ 'geo.site': containsWord }); // geo.country
              or.push({ 'geo.state': containsWord });
              or.push({ 'geo.city': containsWord });
              or.push({ 'geo.location': containsWord });

              return {
                $or: or,
              };
            }),
        })
        .map((result) => result.item);
    }
    return filteredData;
  };
}
